import { ADAPTER_EVENTS, WEB3AUTH_NETWORK } from "@web3auth/base";
import { WALLET_CONNECTORS } from "@web3auth/no-modal";
import { BigNumber, ethers } from "ethers";
import { defineStore } from "pinia";
import { allowWMaticMinAmount, autoUpdateFeq } from "@/application/config";
import { displayError } from "@/utils/errors";
import { useStoreContract } from "./storeContract";
import { toEther } from "~/composables/web3Utils";
import { AlchemyService } from "~/services/AlchemyService";
import { Wallet } from "@/application/entity/wallet";
import { userToken } from "~/utils/localStorage";
import { shallowRef } from "vue";
import { authApi, IRegisterRequest, usersApi } from "~/api/auth";
import {
  EPreferredLanguage,
  PreferredLanguage,
  SocketEventType,
  supportedLanguages,
} from "fungi-types";
import translations from "~/modules/translations";
import { Currency } from "fungi-types";
import { useNotificationCenter } from "~/stores/storeNotificationCenter";
import { useStoreApp } from "./storeApp";
// import { MercureService } from "~/services/mercureService";
import { auctionApi } from "~/api/auction";
import { CreditListResponse } from "~/api/auction/types";
import socketService from "~/services/socketService";
import usePOLPrice from "./POLprice";

import { useWeb3AuthStore } from "@/stores/storeWeb3auth";

export const useStoreUserV2 = defineStore("user-storeV2", {
  state: () => ({
    username: "",
    email: "" as string | null,
    messageToSign: "",
    messageRegisterSigned: "",
    cuid: "",
    optin: false,
    preferredLanguage: null as PreferredLanguage | null,
    preferredCurrency: Currency.USD as Currency,
    migrationToUSDCCompleted: true,
    mustRegister: false,
    createdAt: null as Date | null,
    loading: false,

    // Sponsorship
    referrerId: null as Number | null,
    referralCode: "" as string | null,
    referrerCode: "",
    referralLevel: 0,

    // Alchemy
    alchemy: new AlchemyService(),

    // Wallet Info
    wallet: shallowRef(new Wallet()),
    walletAddress: "",
    smartWalletAddress: "" as `0x${string}`,
    currentMaticBalance: 0,
    currentWMaticBalance: 0,
    currentEthereumBalance: 0,
    currentMaticOnEthBalance: 0,
    refreshTimer: 0,
    lastRefresh: 0,
    messageLoginSigned: "",
    ongoingExpenses: 0,
    allowedWMaticAmount: 0,
    profileDrawerPage: "" as "" | "default" | "settings",
    tokenBalance: 0,
    creditBalance: 0,
    creditList: {
      user: {
        cuid: "",
        username: "",
      },
      credits: [],
      balance: 0,
      activeCredits: [],
    } as CreditListResponse,
  }),
  getters: {
    isConnected: (state) => {
      const web3authStore = useWeb3AuthStore();
      const walletFromWeb3Auth = web3authStore.walletAddress;
      if (!walletFromWeb3Auth) return false;
      return walletFromWeb3Auth.toLowerCase() === state.walletAddress.toLowerCase();
    },
    hasSmartWallet: (state) => {
      const web3authStore = useWeb3AuthStore();
      if (
        state.smartWalletAddress &&
        web3authStore.smartWalletAddress?.toLowerCase() === state.smartWalletAddress.toLowerCase()
      ) {
        return true;
      }
      return false;
    },
    wpolRemaining: (state) => {
      const web3AuthStore = useWeb3AuthStore();
      return web3AuthStore.wpolBalance
        ? Number(web3AuthStore.wpolBalance) - state.ongoingExpenses
        : 0;
    },
    usdcRemaining: (state) => {
      const web3AuthStore = useWeb3AuthStore();
      if (state.smartWalletAddress) {
        return web3AuthStore.usdcBalanceSmartWallet
          ? Number(web3AuthStore.usdcBalanceSmartWallet) - state.ongoingExpenses
          : 0;
      }
      return web3AuthStore.usdcBalance
        ? Number(web3AuthStore.usdcBalance) - state.ongoingExpenses
        : 0;
    },
  },
  actions: {
    async getUserTokenBalance() {
      const { balance } = await auctionApi.users.tokens.balance.get.query({
        cuid: this.cuid,
      });
      this.tokenBalance = balance;
    },
    async getUserCreditBalance() {
      const { balance } = await auctionApi.users.credits.list.query({
        cuid: this.cuid,
      });
      this.creditBalance = balance;
      console.info("[UserStore] User credit balance updated:", this.creditBalance);
    },
    rename(newName: string) {
      this.username = newName;
    },

    async getCreditList() {
      const resp = await auctionApi.users.credits.list.query({
        cuid: this.cuid,
      });
      this.creditList = resp;
    },

    async refreshBalance() {
      const web3authStore = useWeb3AuthStore();
      if (this.isConnected) {
        const userInfo = await authApi.getUserInfo();
        this.tokenBalance = userInfo.tokenBalance;
        this.creditBalance = userInfo.creditBalance;
        await web3authStore.getPolBalance();
        await web3authStore.getWpolBalance();
        await web3authStore.getUsdcBalance();
        this.getOngoingBidAmount();
      }
    },

    async autoRefreshBalance() {
      await this.refreshBalance();

      this.refreshTimer = window.setTimeout(() => {
        this.autoRefreshBalance();
      }, autoUpdateFeq);

      this.lastRefresh = Date.now();
    },

    async decreaseFungiballTokens(amount: number) {
      this.tokenBalance -= amount;
    },

    async initUser() {
      const web3AuthStore = useWeb3AuthStore();
      const userInfo = await auctionApi.users.me.query();
      // const userInfo = await authApi.getUserInfo();
      this.username = userInfo.username;
      this.createdAt = new Date(userInfo.createdAt);
      this.email = userInfo.email;
      this.tokenBalance = userInfo.tokenBalance;
      this.creditBalance = userInfo.creditBalance;
      this.referrerId = userInfo.referralId || null;
      // this.referrerId = userInfo.referral?.referrer_id || null;
      this.referrerCode = userInfo.referralCode || "";
      this.referralLevel = userInfo.referralLevel;
      this.cuid = userInfo.cuid;
      this.optin = userInfo.optin;
      this.preferredLanguage = userInfo.preferredLanguage;
      const preferredCurrency = userInfo.preferredCurrency;
      if (preferredCurrency === Currency.WMATIC && this.migrationToUSDCCompleted) {
        this.preferredCurrency = Currency.USD;
      } else {
        this.preferredCurrency = userInfo.preferredCurrency;
      }

      // this.walletAddress = userInfo.currentWallet.address;
      this.walletAddress = userInfo.walletAddress || "";
      // this.smartWalletAddress = userInfo.smartWalletAddress || "";

      // if (!this.smartWalletAddress) {
      //   useStoreApp().showMigrateToSmartWalletModal = true;
      // }

      if (this.walletAddress.toLowerCase() != web3AuthStore.walletAddress.toLowerCase()) {
        console.log("WALLET ARE NOT SIMILAR, LOGIN OUT...");
        this.logout();
      } else {
        await this.getOngoingBidAmount();

        const storeApp = useStoreApp();

        await this.updatePreferredLanguage(userInfo.preferredLanguage);
        this.getCreditList();
        console.info("[UserStore] User initialized:", this.username);
      }
    },

    async getOngoingBidAmount() {
      const web3AuthStore = useWeb3AuthStore();
      try {
        const ongoingBids = await auctionApi.bid.ongoingAmount.query({
          walletAddress: this.smartWalletAddress || web3AuthStore.walletAddress,
        });
        this.ongoingExpenses = toEther(BigNumber.from(ongoingBids));
      } catch (error) {
        console.error("Error fetching ongoing bids:", error);
        return 0;
      }
    },

    async updatePreferredLanguage(lang: PreferredLanguage, forceApiCall = false) {
      if (lang !== null) {
        this.preferredLanguage = lang;

        if (forceApiCall) {
          usersApi.setUserPreferredLanguage(lang).catch(console.error);
        }
      } else {
        const language =
          (supportedLanguages as EPreferredLanguage[]).find((l) =>
            navigator.language.startsWith(l)
          ) ?? EPreferredLanguage.EN;

        this.preferredLanguage = language;
        await usersApi.setUserPreferredLanguage(language);
      }
    },

    updatePreferredCurrency(currency: Currency) {
      this.preferredCurrency = currency;
      usersApi.setUserPreferredCurrency(currency).catch(console.error);
    },

    async checkUsername(username: string) {
      try {
        const status = await auctionApi.auth.check.username.query({ username });
        return status;
      } catch (error) {
        console.error("Error checking username existence:", error);
        throw error;
      }
    },

    async checkEmail(email: string) {
      try {
        const resp = await auctionApi.auth.check.email.query({ email });
        return resp;
      } catch (error) {
        console.error("Error checking email existence:", error);
        throw error;
      }
    },

    async login() {
      const web3AuthStore = useWeb3AuthStore();
      try {
        const storeApp = useStoreApp();

        this.loading = true;

        let token = userToken.get();

        console.log("🔄 Starting login process...");

        if (!token) {
          // 1. Login with wallet signature
          const auth = await web3AuthStore.verifyWallet();

          // 2. Get language Navigator (if user has to register, it will be used)
          const language =
            (supportedLanguages as EPreferredLanguage[]).find((l) =>
              navigator.language.startsWith(l)
            ) ?? EPreferredLanguage.EN;

          // 3. Call backend to login with wallet signature
          const authResponse = await auctionApi.auth.wallet.login.mutate({
            ...auth,
            web3AuthToken: web3AuthStore.userInfo?.idToken,
            lang: language,
            currency: language === EPreferredLanguage.FR ? Currency.EUR : Currency.USD,
          });

          console.log("🔄 Auth response received:", authResponse);

          // 4. Check login version for web3auth users
          const loginVersion = authResponse.loginVersion;
          if (
            loginVersion == "v1" &&
            web3AuthStore.web3AuthNetwork !== WEB3AUTH_NETWORK.CYAN &&
            web3AuthStore.connectionType !== WALLET_CONNECTORS.METAMASK
          ) {
            console.log("🔴 Web3Auth login version v1 detected, logging out...");
            storeApp.showRedirectToOldAuth = true;
            this.logout();
            return;
          }

          // 5. If success, store token and initialize user store
          console.log("✅ Login with wallet signature successful:");
          userToken.set(authResponse.token);
          token = authResponse.token;
          if (!authResponse.token) {
            throw new Error("Login failed: No token received");
          } else {
            if (authResponse.newUser) storeApp.showRegistrationModal = true;
          }
        }
        if (!token) throw new Error("Missing JWT TOKEN To SignIn");

        // Join user room
        socketService.joinUserRoom(token);

        // Update token balance event listener
        socketService.on(SocketEventType.USER_TOKEN_BALANCE_UPDATED, (data) => {
          if (data.userCuid === this.cuid) this.tokenBalance = data.balance;
        });

        // Update credit balance event listener
        socketService.on(SocketEventType.USER_CREDIT_BALANCE_UPDATED, (data) => {
          if (data.userCuid === this.cuid) this.creditBalance = data.balance;
        });

        await this.initUser();
        web3AuthStore.isInitialized = true;

        await this.autoRefreshBalance();
        await useNotificationCenter().fetchNotifications();

        console.log("✅ login completed");
      } catch (error: any) {
        const status = error.response?.status;
        displayError(error);
        this.logout();
      } finally {
        this.loading = false;
      }
      // web3AuthStore.isInitialized = true;
    },

    async logout() {
      const web3AuthStore = useWeb3AuthStore();
      // await this.wallet.logout();
      userToken.remove();
      this.username = "";
      this.mustRegister = false;
      this.cuid = "";
      this.walletAddress = "";
      await web3AuthStore.logout();
    },

    async register({ optin, username, email, referralCode, utmData }: IRegisterRequest) {
      await authApi.register({
        email: email,
        username,
        referralCode,
        optin,
        preferredLanguage:
          this.preferredLanguage ??
          translations.supportedLocales.find((l) => navigator.language.startsWith(l)) ??
          EPreferredLanguage.EN,
        utmData,
      });
      if (this.mustRegister === true) this.mustRegister = false;
    },

    setRefCode(referralCode: string) {
      sessionStorage.setItem("referralCode", referralCode);
    },

    getReferrerCode() {
      return sessionStorage.getItem("referralCode") || "";
    },
    removeReferrerCode() {
      return sessionStorage.removeItem("referralCode");
    },

    async connectWallet(email: string) {
      const { web3auth } = this.wallet;
      if (web3auth.status === ADAPTER_EVENTS.CONNECTED) await this.wallet.web3auth.logout();
      else if (web3auth.status === ADAPTER_EVENTS.NOT_READY) await this.wallet.web3auth.init();
      else await this.wallet.connect(email);
    },

    async allowWmtc(
      requiredAmount: number = 0,
      options: {
        forceRefresh?: boolean;
        confirmations?: number;
        gasLimit?: number;
      } = {}
    ): Promise<{ success: boolean; allowance: number; txHash?: string }> {
      const storeContract = useStoreContract();
      const web3AuthStore = useWeb3AuthStore();

      // Configuration
      const APPROVAL_BUFFER = 100_000; // Approve extra to reduce future transactions
      const DEFAULT_CONFIRMATIONS = 1;

      const minRequired = Math.max(allowWMaticMinAmount, requiredAmount);
      const approvalAmount = Math.max(APPROVAL_BUFFER, requiredAmount);
      const confirmations = options.confirmations ?? DEFAULT_CONFIRMATIONS;

      try {
        // Check current allowance (with optional cache bypass)
        if (!options.forceRefresh && this.allowedWMaticAmount >= minRequired) {
          return { success: true, allowance: this.allowedWMaticAmount };
        }

        // Refresh allowance from blockchain
        const currentAllowance = await this.fetchCurrentAllowance(
          storeContract,
          web3AuthStore.walletAddress
        );

        this.allowedWMaticAmount = currentAllowance;

        // Recheck after refresh
        if (currentAllowance >= minRequired) {
          return { success: true, allowance: currentAllowance };
        }

        // Execute approval transaction
        console.log(`Approving ${approvalAmount} WMTC for Seaport contract`);

        const tx = await storeContract.allowWmtc(approvalAmount);
        const receipt = await tx.wait(confirmations);

        // Parse approval event safely
        const newAllowance = this.parseApprovalEvent(receipt);

        if (newAllowance !== null) {
          this.allowedWMaticAmount = newAllowance;
        }

        const success = this.allowedWMaticAmount >= minRequired;

        return {
          success,
          allowance: this.allowedWMaticAmount,
          txHash: receipt.transactionHash,
        };
      } catch (error: any) {
        console.error("Failed to approve WMTC:", {
          requiredAmount,
          minRequired,
          error: error instanceof Error ? error.message : error,
        });

        // Rethrow specific errors, return false for general failures
        if (error.code === "ACTION_REJECTED") {
          throw new Error("User rejected the transaction");
        }

        return { success: false, allowance: this.allowedWMaticAmount };
      }
    },

    openProfileDrawer(page: "default" | "settings" = "default") {
      this.profileDrawerPage = page;
    },

    closeProfileDrawer() {
      this.profileDrawerPage = "";
    },
  },
});
